<template>
	<core-layout-content medium>
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("update_binder") }}</template>
		</core-layout-headline-and-actions>

		<!-- Update Form -->
		<j-card class="xs-12 bg5 r2 p4">
			<!-- Loading -->
			<j-spinner v-if="isFetchLoading" />
			<!-- Form -->
			<create-update-form v-else :data="form" @submit="handleSubmit">
				<div
					class="xs-12 flex xs-between mt6"
					slot="actions"
					slot-scope="{ onSubmit }"
				>
					<j-button :isLoading="isUpdateLoading" @click="onSubmit">
						{{ $t("update") }}
					</j-button>
					<j-button
						:isLoading="isDeleteLoading"
						@click="deleteBinder"
						type="error"
					>
						{{ $t("delete") }}
					</j-button>
				</div>
			</create-update-form>
		</j-card>
	</core-layout-content>
</template>

<script>
import JCard from "@/components/JCard/"
import JButton from "@/components/JButton/"
import JSpinner from "@/components/JSpinner/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent.vue"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions.vue"
import CreateUpdateForm from "../components/CreateUpdateForm"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JCard,
		JButton,
		JSpinner,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CreateUpdateForm
	},

	props: {
		id: { type: String, required: true }
	},

	data() {
		return {
			form: {}, // will be populated in fetchBinder
			isFetchLoading: false,
			isUpdateLoading: false,
			isDeleteLoading: false
		}
	},

	methods: {
		async handleSubmit(payload, facts) {
			const query = gql`
				mutation updateBinder($binderId: ID!, $input: binderInput!) {
					updateBinder(binderId: $binderId, input: $input) {
						status
						errors
					}
				}
			`

			const queryUpdateFact = gql`
				mutation updateFactOfBinder(
					$binderId: String!
					$factId: String!
					$input: updateFactInput!
				) {
					updateFactOfBinder(
						binderId: $binderId
						factId: $factId
						input: $input
					) {
						status
						errors
					}
				}
			`

			// 1: Update binder
			// Prepare input
			const input = { ...payload }
			this.isUpdateLoading = true
			const res = await api({
				query,
				variables: {
					binderId: this.id,
					input
				}
			})

			if (res?.data?.updateBinder?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.updateBinder?.errors || this.$t("errors.general")
				)
				this.isUpdateLoading = false
				return
			}

			// 2: Update facts
			const factsRes = await Promise.all(
				facts.map(item =>
					api({
						query: queryUpdateFact,
						variables: {
							binderId: this.id,
							factId: item.id,
							input: { type: item.type, value: item.value }
						}
					})
				)
			)

			// Check errors for facts adding
			if (
				factsRes.find(
					item => item?.data?.updateFactOfBinder?.status !== "success"
				)
			) {
				this.$store.dispatch(
					"showError",
					"There was an error, please try to update this binder again"
				)
				this.isUpdateLoading = false
				return
			}

			// Redirect to detail
			this.isUpdateLoading = false
			this.$router.push({
				name: "BindersList"
			})
		},

		async fetchBinder() {
			const query = gql`
				query binder(
					$where: binderWhereInput
					$sort: sortInput
					$limit: Int
					$skip: Int
				) {
					binders(where: $where, sort: $sort, limit: $limit, skip: $skip) {
						totalCount
						list {
							commissionRule {
								jsonRule
								product {
									name
									productId {
										type
										plan
										version
									}
								}
							}
							facts {
								id
								type
								value {
									stringValue
									numberValue
									booleanValue
									dateValue
								}
							}
							createdAt
							bindingDate
							startDate
							endDate
							firstParty {
								id
								name
							}
							id
							name
							secondParty {
								id
								name
							}
							status
						}
					}
				}
			`

			// Fetch
			this.isFetchLoading = true
			const res = await api({
				query,
				variables: {
					where: { id: this.id }
				}
			})
			this.isFetchLoading = false

			// If list doesn't exist
			if (!res?.data?.binders?.list?.[0]) {
				this.$router.push({ name: "BindersList" })
				return
			}

			// Success
			const data = res?.data?.binders?.list?.[0] || {}
			this.form = { ...data }
		},

		async deleteBinder() {
			const query = gql`
				mutation deleteBinder($binderId: ID!) {
					deleteBinder(binderId: $binderId) {
						status
						errors
					}
				}
			`

			this.isDeleteLoading = true
			const res = await api({ query, variables: { binderId: this.id } })
			this.isDeleteLoading = false

			// Errors
			if (res?.data?.deleteBinder?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.deleteBinder?.errors?.[0] || this.$t("errors.general")
				)
				return
			}

			// Success
			this.$router.push({ name: "BindersList" })
		}
	},

	mounted() {
		this.fetchBinder()
	}
}
</script>
